import {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import axios from "axios";
import { order, backend } from "../../components/apipath";
import { dateHelper } from "../../modules/helpers";
import "./style/admorder.scss";
import Swal from "sweetalert2";


// ======================================================
// parent_id 對照
// ======================================================
const PARENT_LABEL_MAP = {
  1: "CPA",
  2: "CMA",
  3: "EA",
  4: "CPE",
  5: "學分班",
  6: "美稅",
  7: "舊生",
  8: "CIA",
  9: "實務",
  10: "風險自檢",
};


// ======================================================
// 舊 product_id 對照
// parent_id 沒回傳時當 fallback
// ======================================================
const PRODUCT_CATEGORY_MAP = {
  CPA: [1, 2, 3, 4, 5, 29, 31],
  CMA: [6, 7],
  EA: [8, 9, 26, 27],
  學分班: [10],
  美稅: [12, 13, 14, 15, 16, 17, 23, 24],
  CPE: [11, 18],
  舊生: [19, 20, 21],
  CIA: [22, 25, 28],
  風險自檢: [32],
};


// ======================================================
// parent_id → 類別名稱
// ======================================================
function getParentLabel(parentId) {
  const id = Number(parentId);

  return PARENT_LABEL_MAP[id] || "";
}


// ======================================================
// product_id → 類別名稱
// fallback
// ======================================================
function getCategoryByProductId(productId) {
  const id = Number(productId);

  if (!id) {
    return "";
  }

  for (const [label, ids] of Object.entries(PRODUCT_CATEGORY_MAP)) {
    if (ids.includes(id)) {
      return label;
    }
  }

  return "";
}


// ======================================================
// 訂單最終顯示類別
// 1. 優先 parent_id
// 2. fallback product_id
// 3. 都沒有 → 未分類
// ======================================================
function getOrderCategory(row) {
  const parentLabel = getParentLabel(row?.parent_id);

  if (parentLabel) {
    return parentLabel;
  }

  const fallback = getCategoryByProductId(row?.product_id);

  if (fallback) {
    return fallback;
  }

  return "未分類";
}


// ======================================================
// OrderList
// ======================================================
function OrderList() {
  const [orders, setOrders] = useState([]);

  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);

  const [keyword, setKeyword] = useState("");

  // parent_id
  const [parentId, setParentId] = useState("");

  const limit = 50;


  // ====================================================
  // 產品篩選
  // ====================================================
  const [products, setProducts] = useState([]);

  const [prodOpen, setProdOpen] = useState(false);

  const [prodSearch, setProdSearch] = useState("");

  const [productIds, setProductIds] = useState([]);


  // ====================================================
  // 產品搜尋
  // ====================================================
  const filteredProducts = useMemo(() => {
    const kw = prodSearch.trim().toLowerCase();

    if (!kw) {
      return products;
    }

    return products.filter((p) => {
      const productId = String(p.product_id || "");

      const name = String(p.name || "").toLowerCase();

      const category = (
        getParentLabel(p.parent_id) ||
        getCategoryByProductId(p.product_id)
      ).toLowerCase();

      return (
        productId.includes(kw) ||
        name.includes(kw) ||
        category.includes(kw)
      );
    });
  }, [products, prodSearch]);


  // ====================================================
  // 是否全選
  // ====================================================
  const allChecked = useMemo(() => {
    return (
      products.length > 0 &&
      productIds.length === products.length
    );
  }, [products, productIds]);


  // ====================================================
  // 取得訂單
  // ====================================================
  const fetchOrders = async (
    customPage = page,
    ids = productIds,
    customParentId = parentId
  ) => {
    setLoading(true);

    try {
      const resp = await axios.get(`${order}list`, {
        params: {
          page: customPage,
          limit,
          keyword,

          // 類別
          parent_id: customParentId,

          // 個別產品
          product_ids: ids,
          product_ids_csv: ids.join(","),
        },

        paramsSerializer: {
          serialize: (params) => {
            const esc = encodeURIComponent;

            const arr = [];

            for (const k in params) {
              if (Array.isArray(params[k])) {
                params[k].forEach((v) => {
                  arr.push(
                    `${esc(k)}[]=${esc(v)}`
                  );
                });
              } else if (
                params[k] !== "" &&
                params[k] != null
              ) {
                arr.push(
                  `${esc(k)}=${esc(params[k])}`
                );
              }
            }

            return arr.join("&");
          },
        },
      });

      setOrders(
        resp.data.orders ||
        resp.data.data ||
        []
      );

      setTotal(
        resp.data.total ||
        0
      );

      // 如果要檢查後端有沒有 parent_id
      // console.log("orders API =", resp.data.orders);

    } catch (error) {
      console.error(
        "載入訂單資料失敗：",
        error
      );

    } finally {
      setLoading(false);
    }
  };


  // ====================================================
  // 全選 / 清空產品
  // ====================================================
  const toggleAllProducts = (checked) => {
    const next = checked
      ? products.map((p) =>
          String(p.product_id)
        )
      : [];

    setProductIds(next);

    setPage(1);

    fetchOrders(
      1,
      next,
      parentId
    );
  };


  // ====================================================
  // 單一產品
  // ====================================================
  const toggleOneProduct = (
    id,
    checked
  ) => {
    const set = new Set(
      productIds.map(String)
    );

    if (checked) {
      set.add(String(id));
    } else {
      set.delete(String(id));
    }

    const next = Array.from(set);

    setProductIds(next);

    setPage(1);

    fetchOrders(
      1,
      next,
      parentId
    );
  };


  // ====================================================
  // 取得產品清單
  // ====================================================
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${backend}product`
        );

        setProducts(
          Array.isArray(data)
            ? data
            : data?.data || []
        );

      } catch (err) {
        console.error(
          "取得產品清單失敗：",
          err
        );
      }
    })();
  }, []);


  // ====================================================
  // 換頁
  // ====================================================
  useEffect(() => {
    fetchOrders();
  }, [page]);


  // ====================================================
  // 搜尋
  // ====================================================
  const handleSearch = () => {
    setPage(1);

    fetchOrders(
      1,
      productIds,
      parentId
    );
  };


  // ====================================================
  // 清除所有篩選
  // ====================================================
  const clearFilters = () => {
    setKeyword("");

    setParentId("");

    setProductIds([]);

    setProdSearch("");

    setPage(1);

    fetchOrders(
      1,
      [],
      ""
    );
  };


  // ====================================================
  // 修改付款狀態
  // ====================================================
  const handleUpdatePayStatus = async (
    orderId,
    payStatus
  ) => {
    try {
      await axios.put(
        `${backend}order/${orderId}/pay-status`,
        {
          pay_status: payStatus,
        }
      );

      setOrders((prev) =>
        prev.map((o) =>
          o.order_id === orderId
            ? {
                ...o,
                pay_status: payStatus,
              }
            : o
        )
      );

      Swal.fire({
        icon: "success",
        title: "付款狀態已更新",
        timer: 1000,
        showConfirmButton: false,
      });

    } catch (err) {
      console.error(err);

      Swal.fire(
        "更新失敗",
        "",
        "error"
      );
    }
  };


  // ====================================================
  // 刪除訂單
  // ====================================================
  const handleDeleteOrder = async (
    orderId
  ) => {
    const result = await Swal.fire({
      title: "確定要刪除這筆訂單嗎？",
      text: `訂單編號：${orderId}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "是，刪除",
      cancelButtonText: "取消",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${backend}order/${orderId}`
      );

      Swal.fire({
        icon: "success",
        title: "已刪除",
        text: "訂單已成功刪除。",
      });

      fetchOrders();

    } catch (err) {
      console.error(
        "刪除訂單失敗：",
        err
      );

      Swal.fire({
        icon: "error",
        title: "刪除失敗",
        text: "刪除訂單時發生錯誤，請稍後再試。",
      });
    }
  };


  // ====================================================
  // Render
  // ====================================================
  return (
    <div className="container-fluid p-5 mt100">

      <h2>
        訂單管理
      </h2>


      {/* =================================================
          篩選列
      ================================================= */}
      <div className="panel panel-default">

        <div className="panel-body">

          <div className="form-inline clearfix">

            <div className="pull-right order-filter-bar">


              {/* 關鍵字搜尋 */}
              <div
                className="form-group"
                style={{
                  marginRight: 10,
                }}
              >
                <input
                  type="text"
                  className="form-control mb-5"
                  placeholder="搜尋姓名、產品、訂單編號"
                  value={keyword}
                  onChange={(e) =>
                    setKeyword(e.target.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  style={{
                    minWidth: 260,
                  }}
                />
              </div>


              {/* =================================================
                  parent_id 類別搜尋
              ================================================= */}
              <div
                className="form-group"
                style={{
                  marginRight: 10,
                }}
              >

                <select
                  className="form-control mb-5 order-filter-select"
                  value={parentId}
                  onChange={(e) => {
                    const value =
                      e.target.value;

                    setParentId(value);

                    setPage(1);

                    fetchOrders(
                      1,
                      productIds,
                      value
                    );
                  }}
                >

                  <option value="">
                    全部類別
                  </option>

                  <option value="1">
                    CPA
                  </option>

                  <option value="2">
                    CMA
                  </option>

                  <option value="3">
                    EA
                  </option>

                  <option value="4">
                    CPE
                  </option>

                  <option value="8">
                    CIA
                  </option>

                  <option value="5">
                    學分班
                  </option>

                  <option value="6">
                    美稅
                  </option>

                  <option value="7">
                    舊生
                  </option>

                  <option value="9">
                    實務
                  </option>

                  <option value="10">
                    風險自檢
                  </option>

                </select>

              </div>


              {/* =================================================
                  產品多選
              ================================================= */}
              <div
                className="form-group"
                style={{
                  position: "relative",
                  marginRight: 10,
                }}
              >

                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() =>
                    setProdOpen(
                      (v) => !v
                    )
                  }
                >

                  <span>
                    產品篩選（
                    {productIds.length}
                    ）
                  </span>

                  {" "}

                  <span
                    className={`caret ${
                      prodOpen
                        ? "dropup"
                        : ""
                    }`}
                  />

                </button>


                {prodOpen && (

                  <div
                    className="panel panel-default filter-popover"
                    style={{
                      position: "absolute",
                      zIndex: 1050,
                      top: "110%",
                      right: 0,
                      left: "auto",
                      width: 360,
                      maxHeight: 360,
                      overflow: "hidden",
                    }}
                  >


                    {/* header */}
                    <div
                      className="panel-heading"
                      style={{
                        padding: "8px 10px",
                      }}
                    >

                      <div className="input-group input-group-sm">

                        <input
                          type="text"
                          className="form-control"
                          placeholder="搜尋產品名稱、ID或類別"
                          value={prodSearch}
                          onChange={(e) =>
                            setProdSearch(
                              e.target.value
                            )
                          }
                        />

                        <span className="input-group-btn">

                          <button
                            className="btn btn-default"
                            type="button"
                            onClick={() =>
                              setProdSearch("")
                            }
                          >
                            清除
                          </button>

                        </span>

                      </div>


                      <div className="m-t-5">

                        <label
                          className="checkbox-inline"
                          style={{
                            userSelect: "none",
                          }}
                        >

                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={(e) =>
                              toggleAllProducts(
                                e.target.checked
                              )
                            }
                          />

                          {" "}

                          全選 / 清空

                        </label>

                      </div>

                    </div>


                    {/* ============================================
                        產品清單
                    ============================================ */}
                    <div
                      className="panel-body"
                      style={{
                        padding: 0,
                        maxHeight: 230,
                        overflow: "auto",
                      }}
                    >

                      <ul
                        className="list-group"
                        style={{
                          marginBottom: 0,
                        }}
                      >

                        {filteredProducts.map((p) => {

                          const id = String(
                            p.product_id
                          );

                          const checked =
                            productIds
                              .map(String)
                              .includes(id);

                          const category =
                            getParentLabel(
                              p.parent_id
                            ) ||
                            getCategoryByProductId(
                              p.product_id
                            ) ||
                            "未分類";

                          return (

                            <li
                              key={id}
                              className="list-group-item"
                              style={{
                                padding:
                                  "6px 10px",
                              }}
                            >

                              <label
                                style={{
                                  fontWeight:
                                    "normal",

                                  margin: 0,

                                  userSelect:
                                    "none",
                                }}
                              >

                                <input
                                  type="checkbox"
                                  className="m-r-5"
                                  checked={checked}
                                  onChange={(e) =>
                                    toggleOneProduct(
                                      id,
                                      e.target.checked
                                    )
                                  }
                                />

                                {" "}

                                [
                                {category}
                                ]

                                {" "}

                                {p.name}

                              </label>

                            </li>
                          );
                        })}


                        {filteredProducts.length ===
                          0 && (

                          <li className="list-group-item text-muted">
                            無符合產品
                          </li>

                        )}

                      </ul>

                    </div>


                    {/* footer */}
                    <div
                      className="panel-footer text-right"
                      style={{
                        padding: "8px 10px",
                      }}
                    >

                      <button
                        type="button"
                        className="btn btn-default btn-sm m-r-5"
                        onClick={() =>
                          setProdOpen(false)
                        }
                      >
                        關閉
                      </button>


                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => {

                          setProdOpen(false);

                          handleSearch();

                        }}
                      >
                        套用
                      </button>

                    </div>

                  </div>
                )}

              </div>


              {/* 搜尋 */}
              <button
                className="btn btn-primary mb-3"
                onClick={handleSearch}
              >
                搜尋
              </button>


              {" "}


              {/* 清除篩選 */}
              <button
                className="btn btn-default mb-3"
                onClick={clearFilters}
              >
                清除篩選
              </button>

            </div>

          </div>

        </div>

      </div>


      {/* =================================================
          資料表
      ================================================= */}
      {loading ? (

        <p>
          資料載入中...
        </p>

      ) : (

        <>

          <div className="table-responsive">

            <table className="table table-striped border">

              <thead>

                <tr>

                  <th>
                    訂單編號
                  </th>

                  <th>
                    學員
                  </th>

                  <th>
                    產品 / 詳細
                  </th>

                  <th>
                    優惠碼
                  </th>

                  <th>
                    價格
                  </th>

                  <th>
                    訂單狀態
                  </th>

                  <th>
                    匯款日
                  </th>

                  <th>
                    下單日
                  </th>

                  <th>
                    操作
                  </th>

                </tr>

              </thead>


              <tbody>

                {orders.map((o) => (

                  <OrderRow
                    key={o.order_id}
                    row={o}
                    onDelete={
                      handleDeleteOrder
                    }
                    onUpdatePayStatus={
                      handleUpdatePayStatus
                    }
                  />

                ))}

              </tbody>

            </table>

          </div>


          {/* =================================================
              分頁
          ================================================= */}
          <div className="mt-3 text-center">

            <button
              className="btn btn-outline-secondary me-2"
              disabled={page === 1}
              onClick={() =>
                setPage(
                  page - 1
                )
              }
            >
              上一頁
            </button>


            <span>

              第 {page} 頁 / 共{" "}

              {Math.ceil(
                (total || 0) /
                limit
              )}{" "}

              頁

            </span>


            <button
              className="btn btn-outline-secondary ms-2"
              disabled={
                page >=
                Math.ceil(
                  (total || 0) /
                  limit
                )
              }
              onClick={() =>
                setPage(
                  page + 1
                )
              }
            >
              下一頁
            </button>

          </div>

        </>
      )}

    </div>
  );
}


// ======================================================
// 單筆訂單
// ======================================================
function OrderRow({
  row,

  colSpan = 9,

  onDelete,

  onUpdatePayStatus,
}) {

  const [open, setOpen] =
    useState(false);


  const toggle =
    useCallback(
      () =>
        setOpen(
          (v) => !v
        ),
      []
    );


  // ====================================================
  // 計算這筆訂單類別
  // ====================================================
  const category =
    getOrderCategory(row);


  return (

    <>

      {/* =================================================
          主列
      ================================================= */}
      <tr
        className="row-toggle"
        role="button"
        aria-expanded={open}
        tabIndex={0}
        onClick={toggle}

        onKeyDown={(e) => {

          if (
            e.key === "Enter" ||
            e.key === " "
          ) {

            e.preventDefault();

            toggle();

          }
        }}
      >


        {/* 訂單編號 */}
        <td>

          <span
            className={`caret caret-rotate ${
              open
                ? "open"
                : ""
            }`}
          />

          &nbsp;

          {row.order_id}

        </td>


        {/* 學員 */}
        <td>

          <a
            href={
              `/adm/admmemberedit/${row.mem_id}`
            }

            target="_blank"

            rel="noopener noreferrer"

            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {row.student_name}

          </a>

        </td>


        {/* =================================================
            類別 + 產品
        ================================================= */}
        <td>

          <span
            className="label label-default"

            style={{
              display: "inline-block",

              marginRight: 6,

              marginBottom: 4,

              padding: "4px 7px",

              color: "#fff",

              backgroundColor: "#777",

              borderRadius: 3,

              fontSize: 12,
            }}
          >

            {category}

          </span>


          <div
            dangerouslySetInnerHTML={{
              __html:
                row.product_name ||
                "",
            }}
          />

        </td>


        {/* 優惠碼 */}
        <td>

          {getCouponLabelInline(
            row
          )}

        </td>


        {/* 價格 */}
        <td>

          {fmtCurrency(
            row.total_price
          )}

        </td>


        {/* =================================================
            訂單狀態
        ================================================= */}
        <td
          onClick={(e) =>
            e.stopPropagation()
          }
        >

          <select
            className="form-control input-sm order-status-select"

            value={
              row.pay_status ||
              "CONFIRMING"
            }

            onChange={(e) =>
              onUpdatePayStatus(
                row.order_id,
                e.target.value
              )
            }
          >

            <option value="PENDING">
              待付款
            </option>

            <option value="CONFIRMING">
              確認中
            </option>

            <option value="SUCCESS">
              刷卡成功
            </option>

            <option value="COMPLETED">
              完成
            </option>

            <option value="CANCELLED">
              取消
            </option>

            <option value="REFUNDED">
              退費
            </option>

            <option value="FAILED">
              刷卡失敗
            </option>

          </select>

        </td>


        {/* 匯款日 */}
        <td>

          {row.transfer_date
            ? dateHelper(
                row.transfer_date,
                "date"
              )
            : "—"}

        </td>


        {/* 下單日 */}
        <td>

          {row.order_date
            ? dateHelper(
                row.order_date,
                "date"
              )
            : "—"}

        </td>


        {/* 操作 */}
        <td>

          <button
            type="button"
            className="btn btn-danger btn-xs"

            onClick={(e) => {

              e.stopPropagation();

              if (onDelete) {
                onDelete(
                  row.order_id
                );
              }

            }}
          >
            刪除
          </button>

        </td>

      </tr>


      {/* =================================================
          展開資料
      ================================================= */}
      {open && (

        <tr className="row-details">

          <td colSpan={colSpan}>

            <dl
              className="dl-horizontal"
              style={{
                marginBottom: 0,
              }}
            >


              <dt>
                匯款人
              </dt>

              <dd>
                {row.transfer_name ||
                  "—"}
              </dd>


              <dt>
                刷卡編號
              </dt>

              <dd>
                {row.credit_card_order_no ||
                  "-"}
              </dd>


              <dt>
                付費方式
              </dt>

              <dd>
                {row.pay_method ||
                  "—"}
              </dd>


              <dt>
                統編
              </dt>

              <dd>
                {row.tax_num ||
                  "—"}
              </dd>


              <dt>
                公司
              </dt>

              <dd>
                {row.company ||
                  "—"}
              </dd>


              <dt>
                領書選擇
              </dt>

              <dd>
                {fmtDeliver(
                  row.deliver
                )}
              </dd>


              <dt>
                上課方式
              </dt>

              <dd>
                {fmtMode(
                  row.course
                )}
              </dd>


              <dt>
                加購科目
              </dt>

              <dd>
                {row.book_names ||
                  "—"}
              </dd>


              <dt>
                寄送地址
              </dt>

              <dd>
                {row.address ||
                  "—"}
              </dd>


              {row.foreign_address && (

                <>
                  <dt>
                    國外地址
                  </dt>

                  <dd>
                    {row.foreign_address}
                  </dd>
                </>

              )}


              <dt>
                CPA 工作 CV
              </dt>

              <dd>

                <a
                  href={
                    `/adm/admfiles/member/${row.mem_id}/mOther`
                  }

                  target="_blank"

                  rel="noopener noreferrer"

                  onClick={(e) =>
                    e.stopPropagation()
                  }
                >
                  文件
                </a>

              </dd>


              <dt>
                CPA 通過證明
              </dt>

              <dd>

                <a
                  href={
                    `/adm/admfiles/member/${row.mem_id}/mOther`
                  }

                  target="_blank"

                  rel="noopener noreferrer"

                  onClick={(e) =>
                    e.stopPropagation()
                  }
                >
                  文件
                </a>

              </dd>


              <dt>
                一年工作經驗
              </dt>

              <dd>

                {Number(
                  row.agree_cpa_experience
                ) === 1
                  ? "✔ 已確認"
                  : "—"}

              </dd>


              <dt>
                資料真實聲明
              </dt>

              <dd>

                {Number(
                  row.agree_cpa_documents_true
                ) === 1
                  ? "✔ 已確認"
                  : "—"}

              </dd>


              <dt>
                選購科目
              </dt>

              <dd>

                {row.ea_parts ||
                  row.cia_subjects ||
                  "—"}

              </dd>


              <dt>
                備註
              </dt>

              <dd>
                {row.memo ||
                  "—"}
              </dd>


            </dl>

          </td>

        </tr>
      )}

    </>
  );
}


// ======================================================
// 優惠碼
// ======================================================
function getCouponLabelInline(o) {

  if (o?.discount_code) {
    return `${o.discount_code}`;
  }

  if (o?.mou_code) {
    return `${o.mou_code}`;
  }

  return "—";
}


// ======================================================
// 金額
// ======================================================
function fmtCurrency(n) {

  if (n == null) {
    return "—";
  }

  const x =
    Number(n);

  if (Number.isNaN(x)) {
    return String(n);
  }

  return `NT$${x.toLocaleString(
    "zh-TW"
  )}`;
}


// ======================================================
// 領書
// ======================================================
function fmtDeliver(v) {

  if (!v) {
    return "—";
  }

  return Array.isArray(v)
    ? v.join("、")
    : String(v);
}


// ======================================================
// 上課方式
// ======================================================
function fmtMode(v) {

  if (!v) {
    return "—";
  }

  const map = {
    onsite: "現場面授",
    online: "線上",
    hybrid: "混合",
  };

  return (
    map[v] ||
    String(v)
  );
}


export default OrderList;